import { delegatedAccountAbi, dexAbi } from 'sc-interfaces';
import { decodeErrorResult, type Hex } from 'viem';
import type { OrderStatusReason } from '@/api/spec';
import { abiErrorToOrderStatusReason } from '../features/trading';

// DEX ABI first (authoritative), then delegated-account ABI for its own reverts (absent from dexAbi).
const REVERT_DECODE_ABIS = [dexAbi, delegatedAccountAbi] as const;

export interface RevertDecodingParams {
  to: Hex;
  data: Hex;
  from: Hex;
}

export type DecodedScError = {
  errorName: string;
  args: readonly unknown[] | undefined;
  orderStatusReason: OrderStatusReason;
  argsFormatted?: string;
};

/**
 * Helper to construct a DecodedScError object consistently
 * @param errorName The smart contract error name
 * @param args The error arguments
 * @returns Formatted DecodedScError object
 */
function toDecodedScError(errorName: string, args?: readonly unknown[]): DecodedScError {
  const orderStatusReason = abiErrorToOrderStatusReason(errorName);
  const argsFormatted = args?.map((arg) => String(arg)).join(', ');
  return { errorName, args, orderStatusReason, argsFormatted };
}

/**
 * Parses error arguments from an error message string
 * @param message The error message containing arguments in parentheses
 * @returns Parsed arguments array and formatted string, or undefined
 */
function parseArgsFromMessage(message: string): { args: readonly unknown[]; argsFormatted: string } | undefined {
  const argsMatch = message.match(/\(([^)]+)\)/);
  if (!argsMatch || !argsMatch[1]) {
    return undefined;
  }

  const argsString = argsMatch[1];
  try {
    const parsedArgs = argsString.split(',').map((arg: string) => {
      const trimmed = arg.trim();
      if (/^\d+$/.test(trimmed)) {
        return BigInt(trimmed);
      }
      if (trimmed === 'true') return true;
      if (trimmed === 'false') return false;
      return trimmed;
    });
    return { args: parsedArgs, argsFormatted: argsString };
  } catch {
    return { args: undefined as any, argsFormatted: argsString };
  }
}

/**
 * Type guard to check if error is an object
 */
function isErrorObject(error: unknown): error is Record<string, unknown> {
  return typeof error === 'object' && error != null;
}

/**
 * Interface representing viem error structures
 */
interface ViemError {
  name?: string;
  message?: string;
  shortMessage?: string;
  data?: unknown;
  cause?: unknown;
  details?: unknown;
  constructor?: { name?: string };
}

/**
 * Type guard for viem error
 */
function isViemError(error: unknown): error is ViemError {
  return isErrorObject(error);
}

/**
 * Interface for decoded error data from viem
 */
interface DecodedErrorData {
  errorName: string;
  args: readonly unknown[];
  abiItem?: unknown;
}

/**
 * Attempts to decode smart contract revert data, trying the DEX ABI then the delegated-account ABI.
 * @param dataHex The hex-encoded revert data from the transaction
 * @returns Decoded error information or null if decoding fails against every known ABI
 */
function tryDecodeScError(dataHex: Hex | undefined): DecodedScError | null {
  if (!dataHex) return null;

  for (const abi of REVERT_DECODE_ABIS) {
    try {
      const decoded = decodeErrorResult({ abi, data: dataHex });
      return toDecodedScError(decoded.errorName, decoded.args);
    } catch {
      // Selector not in this ABI — try the next one.
    }
  }
  return null;
}

/**
 * Attempts to decode smart contract error from an error object
 * Tries: direct object extraction, hex data decoding, then message parsing as fallback
 * @param error The error object from viem
 * @returns Decoded error information or null if decoding fails
 */
export function tryDecodeError(error: unknown): DecodedScError | null {
  if (!isViemError(error)) return null;

  const revertData = extractRevertData(error);
  if (revertData) {
    if (typeof revertData === 'object' && 'errorName' in revertData) {
      const decodedData = revertData as DecodedErrorData;
      return toDecodedScError(decodedData.errorName, decodedData.args);
    }

    if (typeof revertData === 'string' && revertData.startsWith('0x')) {
      const decoded = tryDecodeScError(revertData);
      if (decoded) {
        return decoded;
      }
    }
  }

  // Fallback: parse error name and args from message string
  const errorName = extractErrorNameFromMessage(error);
  if (errorName) {
    const message = error.shortMessage || error.message || '';
    const parsedArgs = parseArgsFromMessage(message);
    return toDecodedScError(errorName, parsedArgs?.args);
  }

  return null;
}

/**
 * Helper to safely get nested property value
 * @param obj Object to traverse
 * @param path Array of property names to traverse
 * @returns The value at the path or undefined
 */
function getNested(obj: unknown, ...path: string[]): unknown {
  let current = obj;
  for (const key of path) {
    if (!isErrorObject(current) || !(key in current)) {
      return undefined;
    }
    current = current[key];
  }
  return current;
}

/**
 * Checks if a value is a hex string starting with 0x
 */
function isHexString(value: unknown): value is Hex {
  return typeof value === 'string' && value.startsWith('0x');
}

/**
 * Checks if a value is a decoded error data object
 */
function isDecodedErrorData(value: unknown): value is DecodedErrorData {
  return isErrorObject(value) && 'errorName' in value && 'args' in value;
}

// Walks the `cause` chain for usable revert data. viem can nest the raw RPC error several
// causes deep — e.g. Monad reports a reverted eth_call as -32603 (not code 3), leaving the
// revert hex only on the RpcRequestError, three causes below the CallExecutionError.
// Depth-capped to guard against cyclic causes.
const MAX_CAUSE_DEPTH = 10;

function findRevertDataInCauseChain(error: unknown): Hex | DecodedErrorData | undefined {
  let current = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH && isErrorObject(current); depth++) {
    const dataData = getNested(current, 'data', 'data');
    if (isHexString(dataData) || isDecodedErrorData(dataData)) return dataData;
    const data = getNested(current, 'data');
    if (isHexString(data) || isDecodedErrorData(data)) return data;
    current = getNested(current, 'cause');
  }
  return undefined;
}

/**
 * Extracts revert data from various error types that viem might throw
 * @param error The error object from viem
 * @returns The hex-encoded revert data, decoded error object, or undefined
 */
function extractRevertData(error: unknown): Hex | DecodedErrorData | undefined {
  if (!isViemError(error)) return undefined;

  if (error.name === 'ContractFunctionExecutionError' || error.name === 'ContractFunctionRevertedError') {
    const data = getNested(error, 'data');
    if (isDecodedErrorData(data)) {
      return data;
    }

    const dataData = getNested(error, 'data', 'data');
    if (isHexString(dataData)) {
      return dataData;
    }

    const causeData = getNested(error, 'cause', 'data');
    if (isHexString(causeData)) {
      return causeData;
    }

    if (isHexString(data)) {
      return data;
    }
  }

  // Fallback: try other common error structures
  const dataData = getNested(error, 'data', 'data');
  if (dataData) return dataData as Hex | DecodedErrorData;

  const data = getNested(error, 'data');
  if (data) return data as Hex | DecodedErrorData;

  const causeData = getNested(error, 'cause', 'data');
  if (causeData) return causeData as Hex | DecodedErrorData;

  const nestedCauseData = findRevertDataInCauseChain(getNested(error, 'cause'));
  if (nestedCauseData) return nestedCauseData;

  const details = getNested(error, 'details');
  if (isHexString(details)) return details;

  return undefined;
}

/**
 * Extracts error name from ContractFunctionExecutionError message if direct decoding fails
 * @param error The error object from viem
 * @returns The error name if found in the message, or undefined
 */
function extractErrorNameFromMessage(error: unknown): string | undefined {
  if (!isViemError(error)) return undefined;

  const message = error.shortMessage || error.message || '';
  const match = message.match(/(?:Error:\s*)?(\w+)\(/);
  if (match && match[1]) {
    return match[1];
  }

  return undefined;
}

/**
 * Checks if the error is a viem validation error (not a smart contract revert)
 * @param error The error object from viem
 * @returns True if it's a validation error
 */
export function isValidationError(error: unknown): boolean {
  if (!isViemError(error)) return false;

  const errorClassName = error.constructor?.name || error.name || '';

  const validationErrorTypes = [
    'InvalidAddressError',
    'InvalidBytesLengthError',
    'InvalidBooleanError',
    'InvalidBytesError',
    'InvalidHexValueError',
    'InvalidNumberError',
    'InvalidSizeError',
    'InvalidStringLengthError',
    'InvalidTypeError',
    'InvalidValueError',
    'SizeOverflowError',
    'SizeUnderflowError',
  ];

  if (validationErrorTypes.includes(errorClassName)) {
    return true;
  }

  if (errorClassName === 'BaseError' || error.name === 'BaseError') {
    const message = error.shortMessage || error.message || '';
    return message.includes('Invalid') || message.includes('Expected:');
  }

  const message = error.shortMessage || error.message || '';
  return message.includes('Invalid') && message.includes('Expected:');
}

/**
 * Extracts a user-friendly message from viem validation errors
 * @param error The error object from viem
 * @returns A user-friendly error message or undefined
 */
export function getValidationErrorMessage(error: unknown): string | undefined {
  if (isValidationError(error) && isViemError(error)) {
    return `Invalid input: ${error.shortMessage || error.message || 'Invalid input provided'}`;
  }
  return undefined;
}

/**
 * Creates a revertDecoding object for use with useTransactionState
 * @param contractAddress The contract address
 * @param transactionData The encoded transaction data
 * @param fromAddress The sender address
 * @returns RevertDecodingParams object or undefined if any parameter is missing
 */
export function createRevertDecodingParams(
  contractAddress?: Hex,
  transactionData?: Hex,
  fromAddress?: Hex
): RevertDecodingParams | undefined {
  return !contractAddress || !transactionData || !fromAddress
    ? undefined
    : {
        to: contractAddress,
        data: transactionData,
        from: fromAddress,
      };
}

/**
 * Attempts to get revert reason from a reverted transaction by re-simulating it
 * @param publicClient The viem public client
 * @param txHash The transaction hash
 * @param blockNumber The block number where the transaction was mined
 * @param to The contract address
 * @param data The transaction data
 * @param from The sender address
 * @returns Decoded error information or null if decoding fails
 */
export async function getRevertReasonFromTx(
  publicClient: {
    call: (params: { to: Hex; data: Hex; account: Hex; blockNumber: bigint }) => Promise<unknown>;
  },
  txHash: Hex,
  blockNumber: bigint,
  to: Hex,
  data: Hex,
  from: Hex
): Promise<DecodedScError | null> {
  try {
    await publicClient.call({
      to,
      data,
      account: from,
      blockNumber,
    });

    return null;
  } catch (error) {
    return tryDecodeError(error);
  }
}
